import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  accountingChartsApi,
  accountingGeneralLedgerApi,
  accountingJournalDocumentsApi,
  type AccountDto,
  type GeneralLedgerTransactionDto,
  type JournalEntryDocumentDto,
} from '../../services/api';

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(value);
}

function formatDisplayDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

function formatDateForInput(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function exportToCsv(transactions: GeneralLedgerTransactionDto[], fromDate: string, toDate: string) {
  const headers = ['Date', 'Entry #', 'Account', 'Description', 'Debit', 'Credit'];
  const rows = transactions.map((t) => [
    formatDisplayDate(t.date),
    t.entryNumber,
    `${t.accountCode} - ${t.accountName}`,
    `"${(t.description || '').replace(/"/g, '""')}"`,
    t.debit.toFixed(2),
    t.credit.toFixed(2),
  ]);
  const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const dateLabel = fromDate && toDate ? `${fromDate}-to-${toDate}` : 'all-dates';
  a.download = `general-ledger-${dateLabel}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}


interface DocumentsPopoverProps {
  entryId: string;
  entryNumber: string;
  documents: { id: string; name: string; mimeType: string | null; size: number }[];
  anchor: HTMLElement | null;
  onClose: () => void;
}

function DocumentsPopover({ entryId, entryNumber, documents, anchor, onClose }: DocumentsPopoverProps) {
  const [list, setList] = useState<JournalEntryDocumentDto[]>(documents);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (documents.length > 0) {
      setList(documents as JournalEntryDocumentDto[]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    accountingJournalDocumentsApi.list(entryId).then((res) => {
      if (cancelled) return;
      setLoading(false);
      if (res.data) setList(res.data);
    });
    return () => {
      cancelled = true;
    };
  }, [entryId, documents.length]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node) && anchor && !anchor.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [anchor, onClose]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await accountingJournalDocumentsApi.upload(entryId, file);
      if (res.data) setList((prev) => [...prev, res.data!]);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDownload = (doc: JournalEntryDocumentDto) => {
    accountingJournalDocumentsApi.download(doc.id, doc.name).catch(() => {});
  };

  const handleDelete = async (doc: JournalEntryDocumentDto) => {
    await accountingJournalDocumentsApi.delete(doc.id);
    setList((prev) => prev.filter((d) => d.id !== doc.id));
  };

  if (!anchor) return null;

  const rect = anchor.getBoundingClientRect();

  return (
    <div
      ref={ref}
      className="fixed z-50 w-72 rounded-lg border border-gray-200 bg-white shadow-lg py-2"
      style={{ left: rect.left, top: rect.bottom + 4 }}
    >
      <div className="px-3 py-1.5 border-b border-gray-100">
        <p className="text-xs font-medium text-gray-500">Documents for entry {entryNumber}</p>
      </div>
      <div className="max-h-56 overflow-y-auto">
        {loading ? (
          <p className="px-3 py-4 text-sm text-gray-500">Loading…</p>
        ) : list.length === 0 ? (
          <p className="px-3 py-4 text-sm text-gray-500">No documents attached.</p>
        ) : (
          <ul className="py-1">
            {list.map((doc) => (
              <li key={doc.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50">
                <span className="flex-1 truncate text-sm text-gray-700" title={doc.name}>
                  {doc.name}
                </span>
                <button
                  type="button"
                  onClick={() => handleDownload(doc)}
                  className="text-brand-600 hover:text-brand-700 text-xs font-medium"
                >
                  Download
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(doc)}
                  className="text-red-600 hover:text-red-700 text-xs font-medium"
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="border-t border-gray-100 px-3 py-2">
        <label className="inline-flex items-center gap-2 text-sm text-brand-600 hover:text-brand-700 cursor-pointer">
          <input type="file" className="hidden" onChange={handleFileChange} disabled={uploading} />
          {uploading ? 'Uploading…' : 'Add document'}
        </label>
      </div>
    </div>
  );
}

export default function GeneralLedgerPage() {
  const today = useMemo(() => formatDateForInput(new Date()), []);
  const firstOfYear = useMemo(() => {
    const d = new Date();
    d.setMonth(0);
    d.setDate(1);
    return formatDateForInput(d);
  }, []);

  const [accounts, setAccounts] = useState<AccountDto[]>([]);
  const [accountId, setAccountId] = useState<string>('');
  // Default to no date filter so all journal entry records show; user can narrow by date.
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [transactions, setTransactions] = useState<GeneralLedgerTransactionDto[]>([]);
  const [totalDebits, setTotalDebits] = useState(0);
  const [totalCredits, setTotalCredits] = useState(0);
  const [transactionCount, setTransactionCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);
  const [documentsAnchor, setDocumentsAnchor] = useState<{ el: HTMLElement; entryId: string; entryNumber: string; documents: GeneralLedgerTransactionDto['documents'] } | null>(null);

  const fetchAccounts = async () => {
    const res = await accountingChartsApi.list();
    if (res.data) setAccounts(res.data);
  };

  const fetchLedger = async () => {
    setLoading(true);
    setApiError(null);
    try {
      const res = await accountingGeneralLedgerApi.get({
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
        account_id: accountId || undefined,
      });
      if (res.data) {
        setTransactions(res.data.transactions);
        setTotalDebits(res.data.totalDebits);
        setTotalCredits(res.data.totalCredits);
        setTransactionCount(res.data.transactionCount);
      } else {
        setApiError(res.message || 'Failed to load general ledger.');
      }
    } catch {
      setApiError('Failed to load general ledger.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    fetchLedger();
  }, [fromDate, toDate, accountId]);

  const handleExport = () => {
    exportToCsv(transactions, fromDate, toDate);
  };

  return (
    <div className="max-w-7xl mx-auto w-full flex-1 min-h-0 flex flex-col overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-lg font-bold text-gray-900">General Ledger</h1>
          <p className="text-sm text-gray-500 mt-0.5">View detailed transaction history by account.</p>
        </div>
        <button
          type="button"
          onClick={handleExport}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 shrink-0"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Export
        </button>
      </div>

      <div className="flex flex-wrap items-end gap-4 mb-4">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">Account</label>
          <select
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            className="min-w-[200px] rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 outline-none"
          >
            <option value="">All Accounts</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.code} - {a.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">From Date</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 outline-none"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">To Date</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 outline-none"
          />
        </div>
        <button
          type="button"
          onClick={handleExport}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Export
        </button>
      </div>

      {apiError && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {apiError}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <div className="bg-white rounded-xl border border-gray-200 shadow-card p-4 flex items-center gap-3">
          <span className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </span>
          <div>
            <p className="text-xs font-medium text-gray-500">Total Debits</p>
            <p className="text-lg font-semibold text-gray-900">{formatCurrency(totalDebits)}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-card p-4 flex items-center gap-3">
          <span className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </span>
          <div>
            <p className="text-xs font-medium text-gray-500">Total Credits</p>
            <p className="text-lg font-semibold text-gray-900">{formatCurrency(totalCredits)}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-card p-4 flex items-center gap-3">
          <span className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center text-violet-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
          </span>
          <div>
            <p className="text-xs font-medium text-gray-500">Transactions</p>
            <p className="text-lg font-semibold text-gray-900">{transactionCount}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-card flex-1 min-h-0 flex flex-col overflow-hidden">
        <h2 className="text-sm font-semibold text-gray-700 px-4 py-3 border-b border-gray-200">Transaction History</h2>
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-500">
            <svg
              className="w-10 h-10 animate-spin text-brand-500 mb-3"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <p className="font-medium text-gray-600">Loading…</p>
          </div>
        ) : transactions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center text-gray-500">
            <p className="font-medium text-gray-600">No transactions found for the selected criteria.</p>
          </div>
        ) : (
          <div className="overflow-x-auto flex-1 min-h-0">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/80">
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                    Date
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                    Entry #
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                    Account
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                    Description
                  </th>
                  <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                    Debit
                  </th>
                  <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                    Credit
                  </th>
                  <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 w-24">
                    Documents
                  </th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((t) => (
                  <tr key={t.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                    <td className="px-4 py-2.5 text-sm text-gray-700 whitespace-nowrap">
                      {formatDisplayDate(t.date)}
                    </td>
                    <td className="px-4 py-2.5 text-sm font-medium text-gray-900">{t.entryNumber}</td>
                    <td className="px-4 py-2.5 text-sm text-gray-700">
                      {t.accountCode} {t.accountName}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-600 max-w-xs truncate" title={t.description}>
                      {t.description || '—'}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-right tabular-nums text-gray-700">
                      {t.debit > 0 ? formatCurrency(t.debit) : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-right tabular-nums text-gray-700">
                      {t.credit > 0 ? formatCurrency(t.credit) : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDocumentsAnchor((prev) => {
                            if (prev?.entryId === t.entryId && prev?.el === e.currentTarget) return null;
                            return { el: e.currentTarget as HTMLElement, entryId: t.entryId, entryNumber: t.entryNumber, documents: t.documents };
                          });
                        }}
                        className="inline-flex items-center justify-center gap-1 text-brand-600 hover:text-brand-700 text-sm font-medium"
                        title="View documents"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {t.documents.length > 0 ? (
                          <span className="text-xs">({t.documents.length})</span>
                        ) : (
                          <span className="text-xs">Add</span>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {documentsAnchor && (
        <DocumentsPopover
          entryId={documentsAnchor.entryId}
          entryNumber={documentsAnchor.entryNumber}
          documents={documentsAnchor.documents}
          anchor={documentsAnchor.el}
          onClose={() => setDocumentsAnchor(null)}
        />
      )}
    </div>
  );
}
